import { memo, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { MetaPreview } from '../types'
import { applyFocus, rememberedNavIndex } from '../hooks/useRemoteNavigation'
import { TV_ROW_WINDOW, virtualWindow } from '../lib/virtual'
import { MediaCard } from './MediaCard'

interface Props {
  title: string
  items: MetaPreview[]
  onSelect: (item: MetaPreview) => void
  progress?: Record<string, number>
  autofocus?: boolean
  onActivate?: (item: MetaPreview, index: number) => void
  sublabel?: (item: MetaPreview, index: number) => string
  itemProgress?: (item: MetaPreview, index: number) => number | undefined
  navKey?: string
  headerAction?: { label: string; onClick: () => void }
}

type VirtualStyle = CSSProperties & { '--virtual-count': number }

export const MediaRow = memo(function MediaRow({ title, items, onSelect, progress, autofocus, onActivate, sublabel, itemProgress, navKey: explicitNavKey, headerAction }: Props) {
  const navKey = explicitNavKey || `row:${title}`
  const remembered = rememberedNavIndex(navKey)
  const initial = virtualWindow(remembered ?? 0, items.length)
  const [windowStart, setWindowStart] = useState(initial.start)
  const pendingFocus = useRef<number | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const windowEnd = Math.min(items.length, windowStart + TV_ROW_WINDOW)

  useEffect(() => {
    setWindowStart((value) => Math.max(0, Math.min(value, Math.max(0, items.length - TV_ROW_WINDOW))))
  }, [items.length])

  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const shift = (event: Event) => {
      const detail = (event as CustomEvent<{ index: number; direction: 'left' | 'right' }>).detail
      if (!detail || detail.index < 0 || detail.index >= items.length) return
      pendingFocus.current = detail.index
      setWindowStart(virtualWindow(detail.index, items.length, TV_ROW_WINDOW, detail.direction).start)
    }
    row.addEventListener('vela:virtual-focus', shift)
    return () => row.removeEventListener('vela:virtual-focus', shift)
  }, [items.length])

  useEffect(() => {
    const target = pendingFocus.current
    if (target === null) return
    const element = rowRef.current?.querySelector<HTMLElement>(`[data-nav-index="${target}"]`)
    if (!element) return
    pendingFocus.current = null
    applyFocus(element)
  }, [windowStart])

  if (!items.length) return null
  const visible = items.slice(windowStart, windowEnd)
  return (
    <section className="media-section">
      <header className="section-heading">
        <h2>{title}</h2>
        <small>{items.length} {items.length === 1 ? 'title' : 'titles'}</small>
        {headerAction ? <button className="row-header-action" data-focusable onClick={headerAction.onClick}>{headerAction.label}</button> : null}
      </header>
      <div ref={rowRef} className="media-row" data-nav-group="row" data-nav-key={navKey} data-virtual-count={items.length}>
        {windowStart ? <span className="media-row-spacer" aria-hidden="true" style={{ '--virtual-count': windowStart } as VirtualStyle} /> : null}
        {visible.map((item, localIndex) => {
          const index = windowStart + localIndex
          return (
          <MediaCard
            key={`${item.type}:${item.id}:${index}`}
            item={item}
            onSelect={onSelect}
            onActivate={onActivate ? () => onActivate(item, index) : undefined}
            sublabel={sublabel?.(item, index)}
            progress={itemProgress ? itemProgress(item, index) : progress?.[`${item.type}:${item.id}`]}
            virtualIndex={index}
            autofocus={autofocus && index === 0}
          />
          )
        })}
        {windowEnd < items.length ? <span className="media-row-spacer" aria-hidden="true" style={{ '--virtual-count': items.length - windowEnd } as VirtualStyle} /> : null}
      </div>
    </section>
  )
})
